import { ChangeEvent, useEffect, useRef, useState } from "react";
import Datepicker from "../../../../helpers/Datepicker";
import moment from "moment-timezone";
import { format, parseISO } from "date-fns";
import { isEmpty } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";
import TimePicker from "./TimePicker";
import Label from "../Label";
import { Control, useController } from "react-hook-form";
import classNames from "classnames";
import {
  localeDisplayFormat,
  parseUserDate,
} from "../../../../helpers/parseUserDate";

type DateTimePickerArgs = {
  name: string;
  showTimePicker?: boolean;
  label?: string;
  showTimezone?: boolean;
  timeZone?: string;
  futureDatesOnly?: boolean;
  required?: boolean | string;
  control?: Control<any>;
  defaultValue?: string;
  dateAriaLabel?: string;
  timeAriaLabel?: string;
  disabled?: boolean;
  dateClassName?: string;
  dateInputClassName?: string;
};

export default function DateTimePicker({
  name,
  showTimePicker = false,
  label,
  showTimezone = false,
  timeZone,
  futureDatesOnly = false,
  required = false,
  control,
  defaultValue,
  dateAriaLabel,
  timeAriaLabel,
  disabled = false,
  dateClassName,
  dateInputClassName,
}: DateTimePickerArgs) {
  const { field } = useController({
    name,
    rules: { required },
    defaultValue: required ? defaultValue || moment().format() : "",
    control,
  });
  const pickerRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [isManualInput, setIsManualInput] = useState(false);

  useEffect(() => {
    if (!isManualInput) {
      const displayValue = isEmpty(field.value)
        ? ""
        : format(parseISO(field.value), localeDisplayFormat());
      setInputValue(displayValue);
    }
  }, [field.value, isManualInput]);

  function isDateSelectable(pickerDate: string) {
    const momentDate = moment(pickerDate);

    if (futureDatesOnly && momentDate.isBefore(moment().startOf("day"))) {
      return false;
    }

    return true;
  }

  useEffect(() => {
    // Replace vanillajs-datepicker's lenient default parser with our
    // strict one — otherwise the library auto-completes partial input
    // (e.g. "26.11.93" → year 93 literal, "1" → today's month/year)
    // when the field loses focus, overwriting whatever the user typed.
    const displayFormat = localeDisplayFormat();
    const baseOptions = {
      format: {
        toValue: (str: string) => {
          const parsed = parseUserDate(str);
          return parsed ? parsed.getTime() : NaN;
        },
        toDisplay: (date: Date) => format(date, displayFormat),
      },
    };
    const options = futureDatesOnly
      ? {
          ...baseOptions,
          beforeShowDay: (pickerDate: string) => ({
            enabled: isDateSelectable(pickerDate),
          }),
        }
      : baseOptions;

    const picker = new Datepicker(pickerRef.current, options);

    pickerRef.current?.addEventListener("changeDate", (e: CustomEventInit) => {
      field.onChange(
        e.detail.date && moment(e.detail.date).isValid()
          ? moment(e.detail.date)
              .hours(moment(field.value).hours())
              .minutes(moment(field.value).minutes())
              .format()
          : "",
      );
    });

    return () => {
      picker.destroy();
    };
  }, [futureDatesOnly]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Allow numbers, separators, and letters so spelled-out month names
    // (e.g. "4. Juni 1963") work alongside digits.
    const newInputValue = e.target.value;
    setInputValue(newInputValue);
    setIsManualInput(true);

    const parsedDate = parseUserDate(newInputValue);

    if (parsedDate) {
      field.onChange(
        moment(parsedDate)
          .hours(moment(field.value).hours() || 0)
          .minutes(moment(field.value).minutes() || 0)
          .format(),
      );
    }
  };

  const handleBlur = () => {
    setIsManualInput(false);

    const displayValue = isEmpty(field.value)
      ? ""
      : format(parseISO(field.value), localeDisplayFormat());
    setInputValue(displayValue);
  };

  return (
    <div className="date-time-picker-container control-group items-center">
      {label && <Label label={label} required={!!required} />}
      <div className="controls flex gap-2 items-start">
        <div className={classNames("input-group w-36", dateClassName)}>
          <input
            type="text"
            name={name}
            ref={pickerRef}
            className={dateInputClassName}
            placeholder={I18n.t("js.calendars.date_range.date_placeholder")}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            aria-label={dateAriaLabel}
            disabled={disabled}
          />
          <button
            className="btn btn-light btn-sm"
            onClick={(e) => {
              e.preventDefault();
              if (!disabled) {
                pickerRef?.current?.focus();
              }
            }}
            role="button"
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faCalendar} />
          </button>
        </div>
        {showTimePicker && (
          <div className="grid grid-rows-2 grid-cols-1 sm:grid-rows-1 sm:grid-cols-[auto_1fr] gap-2 items-center">
            <TimePicker
              name={field.name}
              onChange={field.onChange}
              value={field.value}
              ariaLabel={timeAriaLabel}
              disabled={disabled}
            />
            <span className="text-sm w-40">
              {showTimezone &&
                timeZone &&
                `UTC ${moment.tz(field.value, timeZone).format("Z")} (${moment
                  .tz(field.value, timeZone)
                  .format("z")})`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
